import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import logo from './assets/general/compliance-graphic.png';

const AlNakhlaHeader = ({ isArabic, setIsArabic }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPastHero, setIsPastHero] = useState(false);

  useEffect(() => {
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.documentElement.lang = isArabic ? 'ar' : 'en';
  }, [isArabic]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsPastHero(scrollPosition > 0);
    };
    
    handleScroll(); // Check initial state
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll); // Recalculate on resize
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navLinks = {
    en: [
      { href: "#about", text: "About Us" },
      { href: "#services", text: "Our Services" },
      { href: "#contact", text: "Contact Us" },
      { href: "#locations", text: "Locations & Branches" }
    ],
    ar: [
      { href: "#about", text: "من نحن" },
      { href: "#services", text: "خدماتنا" },
      { href: "#contact", text: "اتصل بنا" },
      { href: "#locations", text: "المواقع والفروع" }
    ]
  };

  const companyName = {
    en: "Al Nakhla Exchange",
    ar: "شركة النخلة للصرافة"
  };

  const text = {
    en: {
      companyName: companyName.en,
      contactUs: "Contact Us",
      langCode: "AR"
    },
    ar: {
      companyName: companyName.ar,
      contactUs: "اتصل بنا",
      langCode: "EN"
    }
  };

  const currentLang = isArabic ? 'ar' : 'en';
  const direction = isArabic ? 'rtl' : 'ltr';

  const handleContactClick = () => {
    setIsMenuOpen(false);
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
      isPastHero 
        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
        : 'bg-[#006655]/95 backdrop-blur-md'
    }`} style={isPastHero ? {} : {}}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 max-w-7xl">
        <div className="flex h-12 sm:h-14 lg:h-16 items-center justify-between">
          
          <div className="flex items-center">
            <a href="#" className="flex items-center space-x-1 group">
              <img src={logo} alt="Al Nakhla Exchange" className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 object-contain" />
              <span className={`font-bold text-sm sm:text-base lg:text-lg transition-colors ${
                isPastHero ? 'text-gray-800' : 'text-white'
              }`}>{text[currentLang].companyName}</span>
            </a>
          </div>

          <nav className={`hidden md:flex items-center ${isArabic ? 'space-x-6 lg:space-x-8 xl:space-x-10' : 'space-x-4 lg:space-x-6 xl:space-x-6'}`}>
            {navLinks[currentLang].map(link => (
              <a key={link.text} href={link.href} onClick={(e) => {
                e.preventDefault();
                const element = document.querySelector(link.href);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }} className={`text-sm lg:text-base font-medium transition-colors relative group ${isArabic ? 'px-6' : ''} ${
                isPastHero ? 'text-gray-800 hover:text-[#006655]' : 'text-white hover:text-gray-300'
              }`}>
                {link.text}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                  isPastHero ? 'bg-[#006655]' : 'bg-white'
                }`}></span>
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-6 lg:gap-10">
            <button onClick={() => setIsArabic(!isArabic)} className={`flex items-center ${isArabic ? 'space-x-1' : 'gap-1'} text-sm font-medium transition-all ${
              isPastHero ? 'text-gray-800 hover:text-[#006655]' : 'text-white'
            }`}>
              <Globe className="h-4 w-4" />
              <span>{text[currentLang].langCode}</span>
            </button>
            <button onClick={handleContactClick} className={`px-6 lg:px-8 py-2.5 lg:py-3 text-sm lg:text-base font-medium rounded-2xl transition-all shadow-sm hover:shadow-lg transform hover:scale-105`} style={isPastHero ? {backgroundColor: '#006655', color: 'white'} : {backgroundColor: 'white', color: '#006655'}}>
              {text[currentLang].contactUs}
            </button>
          </div>

          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`md:hidden p-2 rounded-md transition-colors ${
            isPastHero ? 'text-gray-800 hover:bg-gray-100' : 'text-white hover:bg-white/10'
          }`} aria-label="Toggle menu">
            {isMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
          </button>
        </div>

        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className={`py-4 border-t ${isPastHero ? 'border-gray-200' : 'border-white/20'}`}>
            <div className="flex flex-col space-y-1">
              {navLinks[currentLang].map(link => (
                <a key={link.text} href={link.href} onClick={(e) => {
                  e.preventDefault();
                  setIsMenuOpen(false);
                  const element = document.querySelector(link.href);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }} className={`px-3 py-2.5 text-sm sm:text-base font-medium rounded-md transition-colors ${
                  isPastHero 
                    ? 'text-gray-800 hover:bg-gray-100' 
                    : 'text-white hover:bg-white/10'
                }`}>
                  {link.text}
                </a>
              ))}
              <div className={`pt-4 mt-2 border-t space-y-2 ${isPastHero ? 'border-gray-200' : 'border-white/20'}`}>
                <button onClick={() => setIsArabic(!isArabic)} className={`w-full flex items-center justify-center space-x-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isPastHero ? 'text-gray-800' : 'text-white'
                }`}>
                  <Globe className="h-4 w-4" />
                  <span>{text[currentLang].langCode}</span>
                </button>
                <button onClick={handleContactClick} className="w-full px-4 py-3 text-base font-medium rounded-2xl transition-colors" style={{backgroundColor: 'white', color: '#006655'}}>
                  {text[currentLang].contactUs}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AlNakhlaHeader;